import { gsap } from "gsap/dist/gsap";

const openers = new Set();

const dimmer = document.querySelector(".mega-menu-dimmer");

const openDimmer = (opener) => {
  openers.add(opener);
  dimmer.classList.add("mega-menu-dimmer--active");
};

const closeDimmer = (opener) => {
  openers.delete(opener);

  if (!openers.size) {
    dimmer.classList.remove("mega-menu-dimmer--active");
  }
};

const zIndices = new Map();
zIndices.set("base", 10);

const getZIndex = (el) => {
  const higherIndex = Math.max(...zIndices.values()) + 1;
  zIndices.set(el, higherIndex);
  return higherIndex;
};

const clearZIndex = (el) => {
  zIndices.delete(el);
};

class MegaMenuOpener extends HTMLElement {
  constructor() {
    super();

    this.closeTimeout = null;
    this.details = this.querySelector("details");
    this.summary = this.querySelector("summary");
    this.close = this.querySelector("[data-mega-menu-close]");
    this.content = this.querySelector("[data-mega-menu-content]");
    this.targets = this.querySelectorAll("[data-animation-item]");

    this.addEventListener("mouseover", () => {
      if (this._isMobile) return;
      this._open();
    });
    this.addEventListener("click", (e) => {
      if (this.summary.contains(e.target)) {
        e.preventDefault();
        this.details.open ? this._close() : this._open();
      }
    });
    this.addEventListener("mouseleave", () => {
      if (this._isMobile) return;
      this._close();
    });
    this.content.addEventListener("focusout", (e) => {
      if (!e.relatedTarget) return;
      if (this.content.contains(e.relatedTarget)) return;
      this._close();
    });

    this.close.addEventListener("click", () => {
      this.querySelectorAll("conditional-details").forEach((el) => {
        el.dispatchEvent(new CustomEvent("conditional-details:close"));
      });
      this._close();
    });

    this.closeEsc = (e) => {
      if (e.key === "Escape" && this.details.open) {
        this._close();
        this.summary.focus();
      }
    };

    document.addEventListener("keydown", this.closeEsc);

    this.handleCloseAllEvent = () => this._close(0);
    document.addEventListener("mega-menu:close-all", this.handleCloseAllEvent);

    const mm = gsap.matchMedia();

    mm.add("(min-width: 1280px)", () => {
      return () => {
        this.content.removeAttribute("style");
      };
    });
  }

  disconnectedCallback() {
    document.removeEventListener(
      "mega-menu:close-all",
      this.handleCloseAllEvent
    );
    document.removeEventListener("keydown", this.closeEsc);
  }

  get openAnimation() {
    if (this._currentAnimation) return this._currentAnimation;
    if (openers.size > 0) return this.menuToMenuOpenAnimation;
    const slideDown = gsap.fromTo(
      this.content,
      {
        height: 0,
      },
      {
        height: this.content.scrollHeight,
        duration: 0.25,
        onStart: () => {
          this.content.style.overflow = "hidden";
        },
        onComplete: () => {
          this.content.style.overflow = null;
        },
        onReverseComplete: () => {
          this.content.style.overflow = null;
        },
      }
    );

    const stagger = gsap.fromTo(
      this.targets,
      {
        opacity: 0,
        y: -20,
      },
      {
        duration: 0.25,
        opacity: 1,
        y: 0,
        stagger: {
          from: 0,
          amount: 0.25,
          grid: "auto",
        },
      }
    );

    const openAnimation = gsap.timeline({
      paused: true,
      onComplete: () => {
        openAnimation.kill();
        this._currentAnimation = null;
      },
      onReverseComplete: () => {
        openAnimation.revert();
        openAnimation.kill();
        this._currentAnimation = null;
      },
    });

    openAnimation.add(slideDown);
    openAnimation.add(stagger, "<0.1");
    this._currentAnimation = openAnimation;
    return this._currentAnimation;
  }

  get closeAnimation() {
    if (this._currentAnimation) return this._currentAnimation;

    const slide = gsap.fromTo(
      this.content,
      {
        height: 0,
      },
      {
        height: this.content.scrollHeight,
        duration: 0.15,
        onStart: () => {
          this.content.style.overflow = "hidden";
        },
        onReverseComplete: () => {
          this.content.style.overflow = null;
        },
      }
    );

    const fade = gsap.fromTo(
      this.targets,
      {
        opacity: 0,
        y: -20,
      },
      {
        duration: 0.3,
        opacity: 1,
        y: 0,
      }
    );

    const closeAnimation = gsap.timeline({
      paused: true,
      onComplete: () => {
        closeAnimation.kill();
        this._currentAnimation = null;
      },
      onReverseComplete: () => {
        closeAnimation.revert();
        closeAnimation.kill();
        this._currentAnimation = null;
      },
    });

    closeAnimation.add(slide);
    closeAnimation.add(fade, "<0.1");

    // For consistency, the close animations always play reversed.
    this._currentAnimation = closeAnimation;
    return this._currentAnimation;
  }

  get menuToMenuOpenAnimation() {
    if (this._currentAnimation) return this._currentAnimation;

    const fadeBackground = gsap.from(this.content, {
      background: "rgba(255,255,255,0)",
      duration: 0.25,
    });

    const fadeTargets = gsap.fromTo(
      this.targets,
      {
        opacity: 0,
      },
      {
        duration: 0.25,
        opacity: 1,
      }
    );

    const menuToMenuOpenAnimation = gsap.timeline({
      paused: true,
      onComplete: () => {
        menuToMenuOpenAnimation.kill();
        this._currentAnimation = null;
      },
      onReverseComplete: () => {
        menuToMenuOpenAnimation.kill();
        menuToMenuOpenAnimation.revert();
        this._currentAnimation = null;
      },
    });

    menuToMenuOpenAnimation.add(fadeBackground);
    menuToMenuOpenAnimation.add(fadeTargets, 0);

    this._currentAnimation = menuToMenuOpenAnimation;
    return this._currentAnimation;
  }

  _open() {
    clearTimeout(this.closeTimeout);
    if (this.details.open && !this._currentAnimation) return;
    this._setNavHeight();
    this.details.open = true;
    window.requestAnimationFrame(() => {
      if (!this._isMobile) {
        this.openAnimation.play();
      }
      this.content.style.zIndex = getZIndex(this);
      openDimmer(this);
      this.dispatchEvent(
        new CustomEvent("mega-menu:opened", { bubbles: true })
      );
    });
  }

  _close(delay = 250) {
    if (!this.details.open) return;

    clearTimeout(this.closeTimeout);

    this.closeTimeout = setTimeout(() => {
      const onClose = () => {
        closeDimmer(this);
        clearZIndex(this);
        this.content.style.zIndex = null;
        if (openers.size === 0) {
          this.dispatchEvent(
            new CustomEvent("mega-menu:closed", { bubbles: true })
          );
        }
        this.details.open = false;
      };
      if (this._isMobile) {
        onClose();
      } else {
        const closeAnimation = this.closeAnimation;

        // If animation is in progress, reverse from current point, otherwise play backwards from end.
        if (closeAnimation.totalProgress() === 0) {
          closeAnimation.reverse(0);
        } else {
          closeAnimation.reverse();
        }
        closeAnimation.then(() => {
          // Confirm actually closed - could be the closeAnimation was a reversed openAnimation which
          // was re-started and is now fully open.
          if (closeAnimation.totalProgress() !== 0) return;

          onClose();
        });
      }
    }, delay);
  }

  get _isMobile() {
    return !window.matchMedia("(min-width: 1280px)").matches;
  }

  _setNavHeight() {
    const nav = this.closest(".header__nav");
    if (!nav) return;

    this.style.setProperty("--nav-height", `${nav.scrollHeight}px`);
  }
}

if (!customElements.get("mega-menu-opener")) {
  customElements.define("mega-menu-opener", MegaMenuOpener);
}
